import { useEffect, useState } from "react"
import { forwardRef, useImperativeHandle, useRef } from "react"
import TagManager from "react-gtm-module"
import { BiSolidEdit } from "react-icons/bi"
import { IoIosInformationCircleOutline } from "react-icons/io"
import { IoAdd, IoClose } from "react-icons/io5"
import { RiDeleteBin6Line } from "react-icons/ri"
import { RxQuestionMarkCircled } from "react-icons/rx"

import { useAlert } from "@fuse/sharedComponents/globalAlert"
import Switch from "@fuse/sharedComponents/Switch"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { EVENT_TICKET_TYPE, PAYMENT_TYPE } from "@letshang/constants"
import { get } from "lodash-es"

import { ELogGTMEvent } from "@firebaseConfig/analyticsUtil"

import {
  useAddTicket,
  useDeleteTicket,
  useGetAllTickets,
  useUpdateTicket,
} from "@api/services/events/useEventsApi"

import TicketPriceInfoBottomSheet, {
  TicketPriceInfoBottomSheetHandle,
} from "./TicketPriceInfoBottomSheet"

const MODE_TYPE_MAP = {
  EDIT: "EDIT",
  CREATE: "CREATE",
} as const

type MODE_TYPE = (typeof MODE_TYPE_MAP)[keyof typeof MODE_TYPE_MAP]

const inputBgClass = "bg-white/10"
const inputClass =
  "w-full rounded-xl text-white p-4 placeholder:text-[#A1A1B3] focus:outline-none border-none text-base font-normal"

const TicketForm = ({
  onSubmit,
  isLoading,
  mode,
  dataToBePreFilled,
}: {
  onSubmit: (ticketData: any) => void
  isLoading: boolean
  mode: MODE_TYPE
  dataToBePreFilled: any
}) => {
  const [price, setPrice] = useState(0)
  const [priceInput, setPriceInput] = useState("")
  const [quantity, setQuantity] = useState(0)
  const [ticketName, setTicketName] = useState("")
  const [ticketDescription, setTicketDescription] = useState("")
  const [requireApproval, setRequireApproval] = useState(false)

  const ticketPriceInfoBottomSheetRef =
    useRef<TicketPriceInfoBottomSheetHandle>(null)

  const handleOpenTicketPriceInfoBottomSheet = () => {
    ticketPriceInfoBottomSheetRef.current?.openModal()
  }
  useEffect(() => {
    if (mode === MODE_TYPE_MAP.EDIT) {
      setPrice(dataToBePreFilled.ticketPrice)
      setPriceInput(dataToBePreFilled.ticketPrice.toString())
      setQuantity(dataToBePreFilled.ticketQuantity)
      setTicketName(dataToBePreFilled.ticketName)
      setTicketDescription(dataToBePreFilled.ticketDescriptions)
      setRequireApproval(dataToBePreFilled.requireApproval)
    }
  }, [mode])

  const handlePriceChange = (value: string) => {
    setPriceInput(value)

    const sanitizedValue = value.replace(/[^0-9.]/g, "").replace(/-/g, "")

    const parts = sanitizedValue.split(".")
    if (parts.length > 2) {
      return
    }

    if (parts[1]?.length > 2) {
      return
    }

    const numValue = Number(sanitizedValue)
    if (!isNaN(numValue) && numValue >= 0) {
      setPrice(numValue)
    }
  }

  const handleQuantityChange = (value: string) => {
    const sanitizedValue = value.replace(/[^0-9]/g, "").replace(/-/g, "")

    const numValue = Number(sanitizedValue)
    if (!isNaN(numValue) && numValue >= 0) {
      setQuantity(numValue)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isQuantityInput =
      e.currentTarget.getAttribute("data-input-type") === "quantity"

    if (e.key === "-" || e.key === "Minus") {
      e.preventDefault()
    }

    // Prevent decimal point for quantity input
    if (isQuantityInput && (e.key === "." || e.key === "Decimal")) {
      e.preventDefault()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Track ticket form submission
    TagManager.dataLayer({
      dataLayer: {
        event: ELogGTMEvent.BUTTON_CLICK,
        button_name: "submit_ticket_form",
        page_name: "TicketModal",
        ticket_name: ticketName,
        ticket_price: price,
        ticket_quantity: quantity,
        ticket_description_length: ticketDescription.length,
        require_approval: requireApproval,
        form_complete: !!(
          ticketName &&
          ticketDescription &&
          price >= 0 &&
          quantity > 0
        ),
      },
    })

    let data = {
      ticketName,
      ticketDescriptions: ticketDescription,
      ticketPrice: price,
      ticketQuantity: quantity,
      requireApproval,
    }
    if (mode === MODE_TYPE_MAP.EDIT) {
      data = {
        ...dataToBePreFilled,
        ...data,
      }
    }
    onSubmit(data)
  }

  return (
    <>
      <form className="flex flex-col gap-6 w-full" onSubmit={handleSubmit}>
        <div>
          <label className="text-white block mb-2 text-left font-medium">
            Ticket name
          </label>
          <input
            className={`${inputClass} ${inputBgClass}`}
            placeholder="e.g. VIP ticket"
            maxLength={40}
            value={ticketName}
            onChange={(e) => setTicketName(e.target.value)}
            required
          />
          <div className="text-right text-xs text-[#A1A1B3]">
            {ticketName.length}/40
          </div>
        </div>
        <div>
          <label className="text-white block mb-2 text-left font-medium">
            Ticket description
          </label>
          <textarea
            className={`${inputClass} ${inputBgClass} min-h-[100px] resize-none`}
            placeholder="e.g. You'll receive access to the VIP lounge"
            maxLength={200}
            value={ticketDescription}
            onChange={(e) => setTicketDescription(e.target.value)}
            required
          />
          <div className="text-right text-xs text-[#A1A1B3]">
            {ticketDescription.length}/200
          </div>
        </div>
        <div>
          <label className="text-white block mb-2 text-left font-medium">
            <span className="flex items-center gap-2">
              Ticket price & availability
              <RxQuestionMarkCircled
                size={22}
                onClick={handleOpenTicketPriceInfoBottomSheet}
              />
            </span>
          </label>
          <div className="flex gap-4">
            <div className="flex-1 flex flex-col items-start gap-1">
              <div
                className={`flex items-center gap-2 ${inputBgClass} rounded-xl p-4 w-full`}
              >
                <span className="text-2xl">🎟️</span>
                <div className="flex flex-col w-full pl-3 border-l border-white/20">
                  <span className="text-[#A1A1B3] text-sm text-left">
                    Price
                  </span>
                  <div className="flex items-center gap-1">
                    {priceInput !== "" && (
                      <span className="text-white text-lg">$</span>
                    )}
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      className="bg-transparent text-white text-lg focus:outline-none w-full"
                      value={priceInput}
                      onChange={(e) => handlePriceChange(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onWheel={(e) => e.currentTarget.blur()}
                      data-input-type="price"
                      placeholder="$1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1 flex flex-col items-start gap-1">
              <div
                className={`flex items-center gap-2 ${inputBgClass} rounded-xl p-4 w-full`}
              >
                <span className="text-2xl">🛒</span>
                <div className="flex flex-col w-full pl-3 border-l border-white/20">
                  <span className="text-[#A1A1B3] text-sm text-left">
                    Total tickets
                  </span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className="bg-transparent text-white text-lg focus:outline-none w-full"
                    value={quantity === 0 ? "" : quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onWheel={(e) => e.currentTarget.blur()}
                    data-input-type="quantity"
                    placeholder="10"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-2">
          <Switch
            label="Require approval"
            checked={requireApproval}
            onChange={(e) => setRequireApproval(e.target.checked)}
            className="py-0"
          />
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full mt-4 bg-white/10 text-white rounded-full py-3 text-lg font-semibold transition ${
            isLoading ||
            !ticketName ||
            !ticketDescription ||
            price < 0 ||
            quantity <= 0
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            `${mode === "CREATE" ? "Create" : "Edit"} ticket`
          )}
        </button>
      </form>
      <TicketPriceInfoBottomSheet
        ref={ticketPriceInfoBottomSheetRef}
        onClose={() => {
          ticketPriceInfoBottomSheetRef.current?.closeModal()
        }}
      />
    </>
  )
}

const TicketList = ({
  tickets,
  onDelete,
  onAddNew,
  eventId,
  handleEditTicket,
}: {
  tickets: any[]
  onDelete: (ticketId: string) => void
  onAddNew: () => void
  eventId: string
  handleEditTicket: ({
    idType,
    id,
  }: {
    idType: "ticketIdInDB" | "tempTicketId"
    id: string
  }) => void
}) => {
  const { showAlert } = useAlert()
  const showDeleteAlert = (ticketId: string) => {
    showAlert(
      "Are you sure you want to delete this ticket?",
      "",
      "Delete ticket",
      () => {
        onDelete(ticketId)
      },
      "Cancel",
      () => {},
    )
  }

  return (
    <div className="flex flex-col w-full h-full">
      <div className="flex-1 flex flex-col gap-4 w-full mb-8">
        {[...tickets]
          .sort((a, b) => a.ticketPrice - b.ticketPrice)
          .map((ticket, idx) => (
            <div
              key={eventId ? ticket.id : ticket.tempId || idx}
              className={`rounded-xl p-4 flex flex-col gap-2 ${inputBgClass}`}
            >
              <div className="flex justify-between items-center pb-2 border-b border-white/20">
                <div className="text-white text-left text-lg font-semibold">
                  {ticket.ticketName}
                </div>
                <div className="flex gap-2">
                  <div
                    className="flex items-center text-sm gap-2 text-white bg-white/10 rounded-full p-2 cursor-pointer"
                    onClick={() => {
                      handleEditTicket({
                        idType: eventId ? "ticketIdInDB" : "tempTicketId",
                        id: eventId ? ticket.id : ticket.tempId,
                      })
                    }}
                  >
                    <BiSolidEdit size={18} />
                  </div>
                  <div
                    className="flex items-center text-sm gap-2 text-white bg-white/10 rounded-full p-2 cursor-pointer"
                    onClick={() => {
                      showDeleteAlert(eventId ? ticket.id : ticket.tempId)
                    }}
                  >
                    <RiDeleteBin6Line className="text-white" />
                  </div>
                </div>
              </div>
              <div className="flex flex-col mt-2 items-start">
                <div className="text-white">${ticket.ticketPrice}</div>
                <div className="text-[#A1A1B3]">
                  {ticket.ticketQuantity} available
                </div>
              </div>
            </div>
          ))}

        <button
          onClick={onAddNew}
          className={`rounded-xl p-4 flex flex-col gap-2 ${inputBgClass} border-2 border-dashed border-white/30 hover:border-white/50 transition-colors cursor-pointer`}
        >
          <div className="flex justify-center items-center py-2">
            <div className="flex items-center gap-3 text-white/70">
              <IoAdd size={24} />
              <span className="text-lg font-medium">Add another ticket</span>
            </div>
          </div>
        </button>
      </div>
      <div className="flex gap-2 text-white/50 text-sm mt-auto">
        <span className="text-xl">ℹ️</span>
        <span className="text-left text-sm">
          Payments are powered by Stripe. Host is responsible for managing
          chargebacks.{" "}
          <a
            href="https://docs.stripe.com/disputes"
            className="!text-white/50 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </span>
      </div>
    </div>
  )
}

// Interface for the modal handle
export interface TicketModalHandle {
  openModal: () => void
  closeModal: () => void
}

const TicketModal = forwardRef<
  TicketModalHandle,
  {
    isOpen?: boolean
    onClose: () => void
    eventId: string
    localTickets?: any[]
    onLocalTicketUpdate?: (tickets: any[]) => void
  }
>(
  (
    {
      isOpen: externalIsOpen,
      onClose,
      eventId,
      localTickets = [],
      onLocalTicketUpdate,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(externalIsOpen || false)

    // Sync internal state with external prop
    useEffect(() => {
      if (externalIsOpen !== undefined) {
        setIsOpen(externalIsOpen)
      }
    }, [externalIsOpen])
    const [isCreatingTicket, setIsCreatingTicket] = useState(false)
    const [isDeletingTicket, setIsDeletingTicket] = useState(false)
    const { mutate: addTicket } = useAddTicket()
    const { mutate: deleteTicket } = useDeleteTicket()
    const { mutate: updateTicket } = useUpdateTicket()
    const { data: ticketsData, refetch: refetchTickets } =
      useGetAllTickets(eventId)
    const apiTickets = get(ticketsData, "data.tickets", [])
    const [mode, setMode] = useState<MODE_TYPE>(MODE_TYPE_MAP.CREATE)
    const [editTicket, setEditTicket] = useState(null)

    // Use local tickets if no eventId, otherwise use API tickets
    const tickets = eventId ? apiTickets : localTickets
    const hasTickets = tickets.length > 0

    // Initialize showForm based on whether tickets exist
    const [showForm, setShowForm] = useState(!hasTickets)

    useEffect(() => {
      if (eventId) {
        refetchTickets()
      }
    }, [eventId])

    // Reset form state when modal opens based on ticket count
    useEffect(() => {
      if (isOpen) {
        setShowForm(!hasTickets)
      }
    }, [isOpen, hasTickets])

    useEffect(() => {
      if (!showForm) {
        setMode(MODE_TYPE_MAP.CREATE)
        setEditTicket(null)
      }
    }, [showForm])

    // Handle back button for ticket modal
    useEffect(() => {
      if (isOpen) {
        const modalId = `ticketmodal-${Date.now()}`

        // Add a dummy entry to history when modal opens
        window.history.pushState(
          { ticketModalOpen: true, modalId },
          "",
          window.location.href,
        )

        // Listen for back button press with higher priority (capture phase)
        const handlePopState = (event: PopStateEvent) => {
          // Close modal when navigating back from our modal state
          if (!event.state?.ticketModalOpen) {
            event.stopImmediatePropagation() // Prevent other handlers
            setShowForm(false)
            setIsOpen(false)
            onClose()
          }
        }

        window.addEventListener("popstate", handlePopState, true) // Use capture phase

        return () => {
          window.removeEventListener("popstate", handlePopState, true)
        }
      }
    }, [isOpen, onClose])

    // Clean up history when modal closes normally (via X button)
    useEffect(() => {
      if (!isOpen && window.history.state?.ticketModalOpen) {
        // Replace current state to remove modal indicator instead of going back
        window.history.replaceState(
          { ...window.history.state, ticketModalOpen: false },
          "",
          window.location.href,
        )
      }
    }, [isOpen])

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      openModal: () => {
        setIsOpen(true)
      },
      closeModal: () => {
        setIsOpen(false)
        onClose()
      },
    }))

    const handleCreate = (ticketData: any) => {
      setIsCreatingTicket(true)

      const newTicket = {
        ticketType:
          ticketData.ticketPrice === 0
            ? EVENT_TICKET_TYPE.FREE_TICKET
            : EVENT_TICKET_TYPE.PAID_TICKET,
        paymentType: PAYMENT_TYPE.MANUAL,
        eventId: eventId,
        ...ticketData,
        isActive: true,
        ...(eventId
          ? {}
          : {
              tempId: `temp-${Date.now()}`,
            }), // Only add temp ID for local tickets
      }

      if (eventId) {
        // API-based creation for existing events
        addTicket(
          {
            eventId,
            ticketData: newTicket,
          },
          {
            onSuccess: async (response) => {
              if (response?.success) {
                showSuccessToast("Ticket created successfully")
                refetchTickets()
                setShowForm(false)
              } else {
                showErrorToast("Something went wrong.")
              }
              setIsCreatingTicket(false)
            },
            onError: () => {
              showErrorToast("Something went wrong.")
              setIsCreatingTicket(false)
            },
          },
        )
      } else {
        // Local storage for events not yet created
        const updatedTickets = [...localTickets, newTicket]
        onLocalTicketUpdate?.(updatedTickets)
        setShowForm(false)
        setIsCreatingTicket(false)
      }
    }

    const handleDelete = (ticketId: string) => {
      setIsDeletingTicket(true)

      if (eventId) {
        // API-based deletion for existing events
        deleteTicket(
          {
            eventId,
            ticketId,
          },
          {
            onSuccess: async () => {
              refetchTickets()
              setIsDeletingTicket(false)
              // Check if this was the last ticket and switch to form if so
              const remainingTickets = apiTickets.filter(
                (ticket) => ticket.id !== ticketId,
              )
              if (remainingTickets.length === 0) {
                setShowForm(true)
              }
            },
            onError: () => {
              showErrorToast("Something went wrong.")
              setIsDeletingTicket(false)
            },
          },
        )
      } else {
        // Local deletion for events not yet created
        const updatedTickets = localTickets.filter(
          (ticket) => ticket.tempId !== ticketId,
        )
        onLocalTicketUpdate?.(updatedTickets)
        setIsDeletingTicket(false)
        // Check if this was the last ticket and switch to form if so
        if (updatedTickets.length === 0) {
          setShowForm(true)
        }
      }
    }

    const handleAddNew = () => {
      // Track add new ticket button click
      TagManager.dataLayer({
        dataLayer: {
          event: ELogGTMEvent.BUTTON_CLICK,
          button_name: "add_new_ticket",
          page_name: "TicketModal",
          event_id: eventId,
          current_tickets_count: tickets.length,
        },
      })
      setShowForm(true)
    }

    const handleModalClose = () => {
      // Reset form state when closing
      setShowForm(false)
      setIsOpen(false)
      onClose()
    }

    const handleEdit = (ticketData: any) => {
      setIsCreatingTicket(true)

      if (eventId && ticketData?.id) {
        const payload = {
          eventId: eventId,
          ticketId: ticketData?.id,
          newTicketData: {
            ...ticketData,
          },
        }
        updateTicket(payload, {
          onSuccess: async (response) => {
            if (response?.success) {
              showSuccessToast("Ticket updated successfully")
              refetchTickets()
            } else {
              showErrorToast("Something went wrong.")
            }
          },
          onError: () => {
            showErrorToast("Something went wrong.")
          },
          onSettled: () => {
            setIsCreatingTicket(false)
            setMode(MODE_TYPE_MAP.CREATE)
            setShowForm(false)
            setEditTicket(null)
          },
        })
      } else {
        // Local storage for events not yet created
        const updatedTickets = localTickets.map((ticket, index) => {
          if (
            (eventId && ticket.id === ticketData.id) ||
            (!eventId && ticket?.tempId === ticketData.tempId)
          ) {
            return {
              ...ticketData,
            }
          } else return ticket
        })
        onLocalTicketUpdate?.(updatedTickets)
        setShowForm(false)
        setIsCreatingTicket(false)
      }
    }

    const handleFormSubmit = (ticketData: any) => {
      switch (mode) {
        case MODE_TYPE_MAP.EDIT:
          handleEdit(ticketData)
          break
        case MODE_TYPE_MAP.CREATE:
        default:
          handleCreate(ticketData)
          break
      }

      // After creating, go back to list view if there are tickets
      if (hasTickets) {
        setShowForm(false)
      }
    }

    const handleEditTicket = ({
      idType,
      id,
    }: {
      idType: "ticketIdInDB" | "tempTicketId"
      id: string
    }) => {
      const ticketToEdit = tickets.filter((ticket: any) => {
        if (idType === "ticketIdInDB") {
          return ticket.id === id
        } else return ticket.tempId === id
      })
      setEditTicket(ticketToEdit[0])
      setMode(MODE_TYPE_MAP.EDIT)
      setShowForm(true)
    }

    const getTitle = (mode: string) => {
      switch (mode) {
        case MODE_TYPE_MAP.EDIT:
          return "Edit ticket"
        case MODE_TYPE_MAP.CREATE:
        default:
          return "New ticket"
      }
    }

    if (!isOpen) return null

    return (
      <div
        className="fixed inset-0 z-50 flex items-end justify-center bg-black/20"
        onClick={handleModalClose}
        style={{ pointerEvents: "auto" }}
      >
        <div
          className="w-full md:max-w-md bg-[#252525B2]/50 backdrop-blur-md rounded-t-[32px] rounded-b-none p-8 pt-6 flex flex-col items-center shadow-2xl animate-slideUp relative max-h-[87%] overflow-y-auto no-scrollbar"
          style={{
            borderTopLeftRadius: 32,
            borderTopRightRadius: 32,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center w-full mb-6">
            <div className="flex-1" />
            <h2 className="flex-1 text-white text-xl font-bold text-center">
              {showForm ? getTitle(mode) : "Tickets"}
            </h2>
            <button
              className="w-[33%] text-white text-3xl p-0 bg-transparent transition flex justify-end items-center"
              onClick={
                showForm
                  ? hasTickets
                    ? () => setShowForm(false)
                    : handleModalClose
                  : handleModalClose
              }
              aria-label="Close"
              type="button"
            >
              <IoClose />
            </button>
          </div>
          <div className="w-full max-w-[400px] mx-auto flex-1">
            {showForm ? (
              <div className="overflow-y-auto h-full">
                <TicketForm
                  onSubmit={handleFormSubmit}
                  isLoading={isCreatingTicket}
                  mode={mode}
                  dataToBePreFilled={editTicket}
                />
              </div>
            ) : (
              <div className="overflow-y-auto h-full">
                <TicketList
                  tickets={tickets}
                  onDelete={handleDelete}
                  onAddNew={handleAddNew}
                  eventId={eventId}
                  handleEditTicket={handleEditTicket}
                />
              </div>
            )}
          </div>
        </div>
        <style>{`
        .animate-slideUp {
          animation: slideUpSheet 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes slideUpSheet {
          from {
            transform: translateY(100%);
            opacity: 0.7;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      </div>
    )
  },
)

export default TicketModal
